/**
 * Flow Regeneration — rest-phrase injection.
 *
 * When a user is stuck AND playing below the accuracy threshold, an easier
 * "rest phrase" is injected into playback to let them recover. Rest phrases are
 * deliberately inert with respect to the complexity model: they receive no
 * verdict, do not move the band, do not earn or lose chevrons, do not count
 * toward progress, and — critically — do NOT consume a PAD slot. They are
 * interleaved into the render queue but are not members of the assessment
 * stream.
 *
 * Everything here is pure so the injection policy can be tested without a
 * scene. Actually inserting the phrase into the render queue is the
 * controller's job (Phase 2).
 */

import {
  AssessmentBand,
  Band,
  TierConfig,
  bandFrom,
  positionBounds,
} from './complexityModel.types';

/** Positions within this distance count as unchanged for stuck-detection.
 *  Positions are `cai`-sized sums, so this only absorbs binary-float residue. */
const FLOOR_EPSILON = 1e-9;

/**
 * Is the user stuck — has the band's position failed to move?
 *
 * ⚠️ **This is not the oracle's `PLATEAU`.** That variable is an anti-jitter
 * cooldown meaning "the band moved on the previous step", which is the
 * *opposite* of stuck; the engineering spec conflates the two and its rest
 * trigger ("plateaued and wma < 95%") reads as nonsense once decoded. This
 * function implements what the trigger was plainly meant to say.
 *
 * Because the cooldown forces a progressing band into a `move → hold → move`
 * cadence, a window of 2 would flag every climbing user as stuck. The default
 * window of 4 spans at least two opportunities to move.
 *
 * @param bands   Trajectory so far, in assessment order.
 * @param config  Supplies `stuckWindow`.
 * @returns `false` until at least `stuckWindow` phrases exist.
 */
export function isStuck(bands: readonly AssessmentBand[], config: TierConfig): boolean {
  if (config.stuckWindow < 2) throw new Error('TierConfig.stuckWindow must be >= 2');
  if (bands.length < config.stuckWindow) return false;

  const window = bands.slice(-config.stuckWindow);
  // Compares POSITION, not crMin. They differ by a constant under either
  // anchoring so the arithmetic is identical today, but position is the value
  // the recurrence actually steps — this is a question about whether the model
  // moved, not about where the band's edge happens to fall.
  const reference = window[0].position;
  return window.every((band) => Math.abs(band.position - reference) < FLOOR_EPSILON);
}

/** Inputs to the rest-phrase decision, gathered by the controller. */
export interface RestContext {
  /** Trajectory so far, in assessment order. */
  bands: readonly AssessmentBand[];
  /** Live accuracy percentage in [0, 100]. See {@link AccuracySource}. */
  accuracy: number;
  /** Assessment phrases played since the last rest phrase (or since session
   *  start). Rest phrases do not increment this — they are not assessments. */
  assessmentsSinceLastRest: number;
}

/**
 * Should a rest phrase be injected before the next assessment phrase?
 *
 * All four conditions must hold:
 *  1. accuracy is below `restAccuracyThreshold` (95) — at or above it the user
 *     is in flow and must not be interrupted (rest phrases are not
 *     chevron-eligible anyway, so interrupting would cost them progress);
 *  2. accuracy is at or above `restAccuracyFloor` (75) — below it the user is
 *     not stuck so much as failing outright, and a rest phrase does not address
 *     it;
 *  3. the user is stuck (see {@link isStuck});
 *  4. at least `restCadence` assessment phrases have elapsed since the last
 *     rest phrase.
 *
 * Conditions 1 and 2 bracket a rest *window* `[75, 95)` rather than a single
 * ceiling. The floor matters because {@link isStuck} is trivially true for
 * anyone pinned at the lower position clamp — the band cannot fall further, so
 * the position stops moving and every subsequent cadence tick would fire.
 * Without the lower bound a struggling user collects rest phrases indefinitely
 * while already on the easiest material the tier has.
 */
export function shouldInjectRest(context: RestContext, config: TierConfig): boolean {
  if (config.restCadence < 1) throw new Error('TierConfig.restCadence must be >= 1');
  // Strictly less, not just "not greater": the window is [floor, threshold), so
  // an equal pair is empty and silently disables rest phrases altogether. Ruled
  // #143 — reject the config rather than ship a dead feature.
  if (config.restAccuracyFloor >= config.restAccuracyThreshold) {
    throw new Error('TierConfig.restAccuracyFloor must be less than restAccuracyThreshold');
  }
  if (context.assessmentsSinceLastRest < 0) {
    throw new Error('assessmentsSinceLastRest must be non-negative');
  }

  if (context.accuracy >= config.restAccuracyThreshold) return false;
  if (context.accuracy < config.restAccuracyFloor) return false;
  if (context.assessmentsSinceLastRest < config.restCadence) return false;
  return isStuck(context.bands, config);
}

/**
 * The complexity band a rest phrase is drawn from: `restPhraseDepth` below the
 * current position, floored at the lowest position the band may occupy, and
 * then widened by the tier's usual anchoring.
 *
 * Anchor-aware on purpose. Dropping the *position* by the depth and re-deriving
 * the band keeps a rest exactly `restPhraseDepth` easier than the phrase before
 * it under either anchoring; subtracting from the band's floor instead would
 * make the effective drop depend on how the band happens to sit.
 *
 * When the position is already near the bottom the rest band bottoms out and
 * overlaps the reserved bottom-of-tier rudimentary pool — intended per the spec.
 */
export function restBand(currentPosition: number, config: TierConfig): Readonly<Band> {
  if (currentPosition < 0 || currentPosition > 1) {
    throw new Error('currentPosition must be in [0, 1]');
  }

  const restPosition = Math.max(
    currentPosition - config.restPhraseDepth,
    positionBounds(config).min,
  );
  return bandFrom(restPosition, config);
}
